// src/pages/agent-booking-system-sabre-integration/index.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseQueries, setupRealtimeSubscription } from '../../utils/supabase';
import Icon from '../../components/AppIcon';
import SearchParameters from './components/SearchParameters';
import ResultsGrid from './components/ResultsGrid';
import BookingQueue from './components/BookingQueue';
import AgentDashboard from './components/AgentDashboard';
import RankingEngine from './components/RankingEngine';
import SabreIntegration from './components/SabreIntegration';

const AgentBookingSystemSabreIntegration = () => {
  const { user, userProfile, tenant, isAgent, canManageBookings } = useAuth();
  
  // State management
  const [activeView, setActiveView] = useState('dashboard');
  const [searchParams, setSearchParams] = useState({
    origin: '',
    destination: '',
    departureDate: '',
    returnDate: '',
    passengers: 1,
    class: 'economy',
    tripType: 'roundtrip'
  });
  const [searchResults, setSearchResults] = useState({
    flights: [],
    hotels: [],
    cars: []
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [agentQueue, setAgentQueue] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [corporateAccounts, setCorporateAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load initial data
  useEffect(() => {
    if (user && userProfile && canManageBookings) {
      loadAgentData();
      setupRealtimeUpdates();
    }
  }, [user, userProfile]);

  const loadAgentData = async () => {
    setLoading(true);
    try {
      // Load agent queue
      if (isAgent) {
        const { data: queue } = await supabaseQueries?.getAgentQueue(user?.id);
        setAgentQueue(queue || []);
      }

      // Load corporate accounts for this tenant
      const { data: accounts } = await supabaseQueries?.getBookings(tenant?.id, {
        status: 'active'
      });
      setCorporateAccounts(accounts || []);

    } catch (error) {
      console.error('Error loading agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeUpdates = () => {
    // Listen for booking updates
    const bookingsChannel = setupRealtimeSubscription(
      'bookings',
      (payload) => {
        console.log('Booking update:', payload);
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
          loadAgentData(); // Refresh data
        }
      },
      { filter: `tenant_id=eq.${tenant?.id}` }
    );

    // Listen for flight inventory updates
    const flightsChannel = setupRealtimeSubscription(
      'flight_inventory',
      (payload) => {
        console.log('Flight inventory update:', payload);
        // Update search results if applicable
        handleInventoryUpdate(payload);
      }
    );

    return () => {
      bookingsChannel?.unsubscribe();
      flightsChannel?.unsubscribe();
    };
  };

  const handleInventoryUpdate = (payload) => {
    if (payload?.eventType === 'UPDATE') {
      setSearchResults(prev => ({
        ...prev,
        flights: prev?.flights?.map(flight => 
          flight?.sabre_flight_id === payload?.new?.sabre_flight_id 
            ? { ...flight, ...payload?.new }
            : flight
        )
      }));
    }
  };

  const handleSearch = async (params) => {
    setLoading(true);
    setSearchParams(params);
    
    try {
      // Search flights
      const { data: flights } = await supabaseQueries?.searchFlights({
        origin: params?.origin,
        destination: params?.destination,
        departureDate: params?.departureDate,
        returnDate: params?.returnDate
      });

      // Search hotels
      const { data: hotels } = await supabaseQueries?.searchHotels({
        location: params?.destination,
        checkIn: params?.departureDate,
        checkOut: params?.returnDate || params?.departureDate
      });

      setSearchResults({
        flights: flights || [],
        hotels: hotels || [],
        cars: [] // Car rental integration would go here
      });

      setActiveView('results');
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSelection = async (booking) => {
    setSelectedBooking(booking);
    
    // If this is an agent, add to their queue
    if (isAgent && booking) {
      try {
        await supabaseQueries?.assignBookingToAgent(booking?.id, user?.id, 1);
        loadAgentData(); // Refresh queue
      } catch (error) {
        console.error('Error assigning booking:', error);
      }
    }
  };

  const renderSidebar = () => (
    <div className="w-80 bg-surface border-r border-border flex flex-col h-full">
      {/* Agent Info Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <Icon name="User" size={24} className="text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-heading-semibold text-text-primary">
              {userProfile?.full_name}
            </h2>
            <p className="text-sm text-text-secondary">
              {userProfile?.role === 'agent' ? 'Travel Agent' : 'Travel Operations'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <div className="space-y-2">
          {[
            { id: 'dashboard', label: 'Agent Dashboard', icon: 'BarChart3' },
            { id: 'search', label: 'Search & Book', icon: 'Search' },
            { id: 'queue', label: 'Booking Queue', icon: 'Clock', badge: agentQueue?.length },
            { id: 'corporate', label: 'Corporate Accounts', icon: 'Building2' },
            { id: 'ranking', label: 'Ranking Engine', icon: 'TrendingUp' },
            { id: 'sabre', label: 'Sabre Integration', icon: 'Zap' }
          ]?.map((item) => (
            <button
              key={item?.id}
              onClick={() => setActiveView(item?.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-body-medium transition-colors ${
                activeView === item?.id
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' :'text-text-secondary hover:bg-secondary-50 hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon name={item?.icon} size={18} />
                <span>{item?.label}</span>
              </div>
              {item?.badge && (
                <span className="px-2 py-1 bg-primary-600 text-white text-xs rounded-full">
                  {item?.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Quick Stats */}
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="p-2 bg-green-50 rounded-lg">
            <div className="text-lg font-heading-semibold text-green-700">
              {performanceMetrics?.bookingsToday || 0}
            </div>
            <div className="text-xs text-green-600">Today</div>
          </div>
          <div className="p-2 bg-blue-50 rounded-lg">
            <div className="text-lg font-heading-semibold text-blue-700">
              {performanceMetrics?.avgResponseTime || '2.5'}m
            </div>
            <div className="text-xs text-blue-600">Avg Time</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMainContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <AgentDashboard
            user={userProfile}
            tenant={tenant}
            metrics={performanceMetrics}
            corporateAccounts={corporateAccounts}
            onRefresh={loadAgentData}
          />
        );
      case 'search':
        return (
          <div className="flex h-full">
            <SearchParameters
              searchParams={searchParams}
              onSearch={handleSearch}
              loading={loading}
            />
            <ResultsGrid
              results={searchResults}
              onBookingSelect={handleBookingSelection}
              selectedBooking={selectedBooking}
              userProfile={userProfile}
            />
          </div>
        );
      case 'queue':
        return (
          <BookingQueue
            queue={agentQueue}
            onItemSelect={setSelectedBooking}
            selectedItem={selectedBooking}
            userProfile={userProfile}
            onRefresh={loadAgentData}
          />
        );
      case 'corporate':
        return (
          <div className="p-6">
            <div className="max-w-6xl mx-auto">
              <div className="mb-6">
                <h1 className="text-2xl font-heading-bold text-text-primary">Corporate Accounts</h1>
                <p className="text-text-secondary">Manage assigned corporate clients and their booking preferences</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {corporateAccounts?.slice(0, 6)?.map((account) => (
                  <div key={account?.id} className="bg-surface rounded-lg border border-border p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading-semibold text-text-primary">
                          {account?.trip_request?.title || 'Corporate Account'}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {account?.traveler?.full_name}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-body-medium ${
                        account?.status === 'confirmed' ?'bg-green-100 text-green-800'
                          : account?.status === 'pending' ?'bg-yellow-100 text-yellow-800' :'bg-gray-100 text-gray-800'
                      }`}>
                        {account?.status}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Type:</span>
                        <span className="text-text-primary capitalize">{account?.booking_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Value:</span>
                        <span className="text-text-primary font-body-medium">
                          ${account?.total_cost?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Travel Date:</span>
                        <span className="text-text-primary">
                          {new Date(account?.travel_date)?.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-border">
                      <button className="w-full px-4 py-2 bg-primary-50 text-primary-700 rounded-lg font-body-medium hover:bg-primary-100 transition-colors">
                        Manage Account
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'ranking':
        return (
          <RankingEngine
            tenant={tenant}
            searchResults={searchResults}
            onResultsRanked={setSearchResults}
          />
        );
      case 'sabre':
        return (
          <SabreIntegration
            tenant={tenant}
            onInventoryUpdate={handleInventoryUpdate}
          />
        );
      default:
        return null;
    }
  };

  if (!user || !canManageBookings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Lock" size={48} className="text-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-heading-semibold text-text-primary mb-2">
            Access Restricted
          </h2>
          <p className="text-text-secondary">
            This area is restricted to travel agents and operations staff.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Plane" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-xl font-heading-semibold text-text-primary">
                  Agent Booking System
                </h1>
                <p className="text-sm text-text-secondary">
                  Sabre GDS Integration • {tenant?.name}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Wifi" size={16} className="text-green-500" />
                <span>Sabre Connected</span>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Clock" size={16} />
                <span>SLA: 24h</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex h-[calc(100vh-80px)]">
        {renderSidebar()}
        <div className="flex-1 overflow-auto">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};

export default AgentBookingSystemSabreIntegration;